import { useNavigate } from 'react-router-dom';
import { forwardRef, useImperativeHandle } from 'react';

import { createPracticePayers } from 'api/Payer';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { usePayersSearch } from 'lib/hooks/queries/payers/usePayersSearch';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';

import { newPayerColumns } from '../lib/columns';
import { PAYER_SEARCH_FILTERS } from '../lib/filters';

const AddPayerTable = forwardRef(({ practiceId, handleClose, setSelectedRows }, ref) => {
  const navigate = useNavigate();

  const { mutate: addPayers, isLoading: isAdding } = useCustomMutation({
    mutationFn: async (payerIds) => createPracticePayers(navigate, { payerIds, practiceId }),
    successMessage: 'Payers added',
    errorMessage: "Couldn't add payers",
    invalidateQueryKey: ['payers', 'payers-search'],
    onSuccess: () => handleClose?.()
  });

  useImperativeHandle(ref, () => ({
    handleAddPayers: (selectedRows) => {
      if (selectedRows.length > 0) {
        const payerIds = selectedRows.map((row) => row.data?.id || row.id).filter(Boolean);
        addPayers(payerIds);
      }
    },
    isLoading: isAdding
  }));

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-base font-500 text-neutral-900">Select payer</p>
        <p className="text-sm text-neutral-700">
          Search and select payers from the list. If you don&apos;t find the payer you need, you can
          request a new one.
        </p>
      </div>

      <div className="flex h-full flex-col overflow-hidden rounded-md border border-solid border-neutral-200">
        <AgTablePage
          noHeader
          title="Payers"
          name="add-payer-modal"
          queryKey="payers"
          useData={usePayersSearch}
          defaultColumns={newPayerColumns}
          defaultFilters={PAYER_SEARCH_FILTERS}
          onSelect={setSelectedRows}
        />
      </div>
    </div>
  );
});

AddPayerTable.displayName = 'AddPayerTable';

export default AddPayerTable;
